import type { TodoistItem, TodoistProject, TodoistSnapshot } from './types'
import { translateRecurrence } from './recurrence'
import { projectIconColor } from './colors'
import { MAX_SUBTASK_DEPTH } from '../../db/taskTree'
import { MAX_PROJECT_DEPTH } from '../../projectTree'
import { reminderFate } from './reminders'
import { mapDate } from './mapDate'

/**
 * **Was ein Import mitbringt — vorher, mit Zahlen** (T05, #170).
 *
 * Reine Rechnung über den Schnappschuss; die Seite zeigt nur an. Die
 * Zuordnung folgt `docs/concept/todoist-import.md` („Was ankommt“, „Was nur
 * halb ankommt“, „Was nicht ankommt“).
 */

export type PreviewLineKey =
  // kommt an
  | 'projects'
  | 'sections'
  | 'tasks'
  | 'quickNotes'
  | 'completed'
  | 'subtasks'
  | 'subprojects'
  | 'favorites'
  | 'tags'
  | 'dated'
  | 'deadlines'
  | 'durations'
  | 'priorities'
  | 'recurringKept'
  | 'projectComments'
  | 'reminders'
  | 'autoReminders'
  // kommt verändert an
  | 'deepSubprojects'
  | 'deepSubtasks'
  | 'comments'
  | 'recurringDropped'
  | 'inboxSections'
  | 'urgentReminders'
  // kommt nicht an
  | 'attachments'
  | 'inboxComments'
  | 'locationReminders'
  | 'reminderRhythms'
  | 'othersReminders'
  | 'filters'
  | 'collaborators'

export interface PreviewLine {
  key: PreviewLineKey
  /** `null` bei festen Zeilen, die Todoist nicht zählt (Erinnerungen, Filter …). */
  count: number | null
  /** Weitere Zahlen für den Text, z. B. `archived` bei den Projekten. */
  values?: Record<string, number>
}

export interface PreviewProject {
  id: string
  /** Mit Weg bei Unterprojekten: „Haus / Garten“. */
  name: string
  openTasks: number
  archived: boolean
  /** Todoists Inbox — landet in „Kurz notiert“. */
  inbox: boolean
  /** Farbe des künftigen `#` (T08); `null` = Ton des Lebensbereichs. */
  iconColor: string | null
}

export interface ImportPreview {
  arrives: PreviewLine[]
  changed: PreviewLine[]
  missing: PreviewLine[]
  projects: PreviewProject[]
}

/** Was HappyTask grundsätzlich nicht kennt — ohne Zahl, weil der Abruf es gar nicht holt. */
const ALWAYS_MISSING: PreviewLineKey[] = ['filters', 'collaborators']

/**
 * Tiefe einer Aufgabe: 0 = Aufgabe, 1 = Unteraufgabe, 2+ = tiefer. Eine
 * Elternaufgabe, die nicht im Schnappschuss liegt (erledigt, gelöscht), macht
 * die Zeile zur Aufgabe — sie kommt ja auch als solche an.
 */
function depthOf(item: TodoistItem, byId: Map<string, TodoistItem>): number {
  let depth = 0
  let current = item
  const seen = new Set<string>()
  while (current.parent_id && byId.has(current.parent_id) && !seen.has(current.id)) {
    seen.add(current.id)
    current = byId.get(current.parent_id)!
    depth++
  }
  return depth
}

/** Tiefe eines Projekts: 0 = oben, 1 = Unterprojekt, … — wie {@link depthOf}. */
function projectDepthOf(project: TodoistProject, byId: Map<string, TodoistProject>): number {
  let depth = 0
  let current = project
  const seen = new Set<string>()
  while (current.parent_id && byId.has(current.parent_id) && !seen.has(current.id)) {
    seen.add(current.id)
    current = byId.get(current.parent_id)!
    depth++
  }
  return depth
}

/** Alle Zeilen über `item`, vom Elternteil bis zur Aufgabe ganz oben. */
function ancestorsOf(item: TodoistItem, byId: Map<string, TodoistItem>): TodoistItem[] {
  const chain: TodoistItem[] = []
  let current = item
  const seen = new Set<string>()
  while (current.parent_id && byId.has(current.parent_id) && !seen.has(current.id)) {
    seen.add(current.id)
    current = byId.get(current.parent_id)!
    chain.push(current)
  }
  return chain
}

/** „Haus / Garten“ — in der Liste der Vorschau, damit die Wahl des Lebensbereichs weiß, welches Projekt gemeint ist. */
function pathOf(project: TodoistProject, byId: Map<string, TodoistProject>): string {
  const names = [project.name]
  const seen = new Set([project.id])
  let current = project
  while (current.parent_id && byId.has(current.parent_id) && !seen.has(current.parent_id)) {
    current = byId.get(current.parent_id)!
    seen.add(current.id)
    names.unshift(current.name)
  }
  return names.join(' / ')
}

/** Eltern vor Kindern, sonst in Todoists Reihenfolge; die Inbox zuerst. */
function orderProjects(projects: TodoistProject[]): TodoistProject[] {
  const children = new Map<string | null, TodoistProject[]>()
  const ids = new Set(projects.map(p => p.id))
  for (const project of projects) {
    const parent = project.parent_id && ids.has(project.parent_id) ? project.parent_id : null
    children.set(parent, [...(children.get(parent) ?? []), project])
  }
  const ordered: TodoistProject[] = []
  const visit = (parent: string | null) => {
    const list = [...(children.get(parent) ?? [])].sort((a, b) =>
      Number(b.inbox_project) - Number(a.inbox_project) || Number(a.is_archived) - Number(b.is_archived) || a.child_order - b.child_order)
    for (const project of list) {
      ordered.push(project)
      visit(project.id)
    }
  }
  visit(null)
  return ordered
}

const line = (key: PreviewLineKey, count: number, values?: Record<string, number>): PreviewLine =>
  values ? { key, count, values } : { key, count }

export function buildImportPreview(snapshot: TodoistSnapshot): ImportPreview {
  const itemsById = new Map(snapshot.items.map(i => [i.id, i]))
  const projectsById = new Map(snapshot.projects.map(p => [p.id, p]))
  const inboxIds = new Set(snapshot.projects.filter(p => p.inbox_project).map(p => p.id))
  const projects = snapshot.projects.filter(p => !p.inbox_project)

  const depth = new Map(snapshot.items.map(i => [i.id, depthOf(i, itemsById)]))
  const tasks = snapshot.items.filter(i => depth.get(i.id) === 0)
  // Bis Ebene 4 bleibt die Tiefe (#181); was darunter liegt, rückt hoch.
  const subtasks = snapshot.items.filter(i => { const d = depth.get(i.id) ?? 0; return d >= 1 && d <= MAX_SUBTASK_DEPTH })
  const deeper = snapshot.items.filter(i => (depth.get(i.id) ?? 0) > MAX_SUBTASK_DEPTH)
  // Unteraufgaben tragen seit T14 (#179) Datum, Deadline, Dauer und Priorität mit —
  // sie zählen bei diesen Zeilen mit. Ihre Wiederholung bauen sie seit #205 nach, außer eine
  // Ebene darüber wiederholt sich (dann geht sie in die Beschreibung).
  const allSubtasks = [...subtasks, ...deeper]
  const all = [...tasks, ...allSubtasks]

  const recurring = tasks.filter(t => t.due?.is_recurring)
  const recurs = (item: TodoistItem) => !!item.due?.is_recurring && translateRecurrence(item.due) !== null
  const recurringSubtasks = allSubtasks.filter(i => i.due?.is_recurring)
  const keptSubtasks = recurringSubtasks.filter(i => recurs(i) && !ancestorsOf(i, itemsById).some(recurs))
  const attachments =
    snapshot.notes.filter(n => n.file_attachment).length + snapshot.projectNotes.filter(n => n.file_attachment).length

  // Erinnerungen (Plan task-reminders): nur an offenen Aufgaben in offenen Projekten — wie der Plan.
  const reminders = snapshot.reminders.filter(r => {
    const item = itemsById.get(r.item_id)
    return item && !projectsById.get(item.project_id)?.is_archived
  })
  const fates = reminders.map(r => ({ reminder: r, fate: reminderFate(r, snapshot.userId) }))
  const arriving = fates.filter(f => f.fate === 'arrives')
  // Todoists automatische Erinnerung gibt Todoist nicht heraus — der Import setzt die Standard-Erinnerung (`withAutoReminder`).
  const withOwnBefore = new Set(arriving.filter(f => f.reminder.type === 'relative').map(f => f.reminder.item_id))
  const autoReminders = snapshot.autoReminder === null ? 0 : snapshot.items.filter(item =>
    item.due && mapDate(item.due.date)?.time && !withOwnBefore.has(item.id) && !projectsById.get(item.project_id)?.is_archived).length

  const arrives = [
    line('projects', projects.length, { archived: projects.filter(p => p.is_archived).length }),
    // Archivierte Abschnitte (#193) kommen erst mit dem Verlauf dazu — nur die, auf die Erledigtes verweist.
    line('sections', snapshot.sections.filter(s => !inboxIds.has(s.project_id)).length, {
      archived: snapshot.sections.filter(s => !inboxIds.has(s.project_id) && s.is_archived).length,
    }),
    line('tasks', tasks.filter(t => !inboxIds.has(t.project_id)).length),
    line('quickNotes', tasks.filter(t => inboxIds.has(t.project_id)).length),
    line('completed', snapshot.completedItems.length),
    line('subtasks', subtasks.length),
    // Unterprojekte kommen als Unterprojekte an (#185), bis Ebene 4.
    line('subprojects', projects.filter(p => { const d = projectDepthOf(p, projectsById); return d >= 1 && d < MAX_PROJECT_DEPTH }).length),
    // Todoists Projekt-Favoriten werden Favoriten (#194); Label-Favoriten nicht — HappyTask favorisiert keine Tags.
    line('favorites', projects.filter(p => p.is_favorite).length),
    line('tags', snapshot.labels.length),
    line('dated', all.filter(t => t.due && !t.due.is_recurring).length),
    line('deadlines', all.filter(t => t.deadline).length),
    line('durations', all.filter(t => t.duration).length), // Tage als Aufwand zu je 8 Std. (#197)
    line('priorities', all.filter(t => t.priority > 1).length),
    line('recurringKept', recurring.filter(t => translateRecurrence(t.due)).length + keptSubtasks.length),
    line('comments', snapshot.notes.length),
    // Kommentare an Projekten (#184) — die der Inbox nicht: „Kurz notiert“ ist kein Projekt.
    line('projectComments', snapshot.projectNotes.filter(n => !inboxIds.has(n.project_id)).length),
    line('reminders', arriving.length),
    line('autoReminders', autoReminders),
  ]
  const changed = [
    line('deepSubprojects', projects.filter(p => projectDepthOf(p, projectsById) >= MAX_PROJECT_DEPTH).length),
    line('deepSubtasks', deeper.length),
    line('recurringDropped', recurring.filter(t => !translateRecurrence(t.due)).length + recurringSubtasks.length - keptSubtasks.length),
    line('inboxSections', snapshot.sections.filter(s => inboxIds.has(s.project_id)).length),
    // „Dringend“ durchbricht in Todoist Stumm — hier wird es eine normale Erinnerung.
    line('urgentReminders', arriving.filter(f => f.reminder.is_urgent).length),
  ]
  const missing: PreviewLine[] = [
    line('attachments', attachments),
    line('inboxComments', snapshot.projectNotes.filter(n => inboxIds.has(n.project_id)).length),
    line('locationReminders', snapshot.locationReminders),
    line('reminderRhythms', fates.filter(f => f.fate === 'rhythm').length),
    line('othersReminders', fates.filter(f => f.fate === 'otherPerson').length),
    ...ALWAYS_MISSING.map(key => ({ key, count: null })),
  ]

  const openByProject = new Map<string, number>()
  for (const task of tasks) openByProject.set(task.project_id, (openByProject.get(task.project_id) ?? 0) + 1)

  return {
    arrives: arrives.filter(l => l.count !== 0),
    changed: changed.filter(l => l.count !== 0),
    missing: missing.filter(l => l.count !== 0),
    projects: orderProjects(snapshot.projects).map(p => ({
      id: p.id,
      name: p.inbox_project ? p.name : pathOf(p, projectsById),
      openTasks: openByProject.get(p.id) ?? 0,
      archived: p.is_archived,
      inbox: p.inbox_project,
      iconColor: p.inbox_project ? null : projectIconColor(p.color),
    })),
  }
}
